import { Series, SeriesPoint } from "@/types/common";
import {
  PREFIX_COMPOUND_METRIC_PREFIX,
  PREFIX_COMPOUND_METRICS,
  COMPOUND_METRICS_PROCESSED_STATS,
} from "@/config/series";

// Find the compound stats series point which matches
// the prefix point at an index.
// There will generally be fewer prefix stats points than compound stats points.
// Timestamp intervals can be inconsistent, so starting
// from the first matching timestamp is insufficient.
// Matching by timestamp, to the minute (assuming minutes are consistent enough)
// e.g. compound stat               - prefix stat
//      2020-01-07T16:31:26.368201Z - 2020-01-07T16:31:27.496081Z
//      matched on '2020-01-07T16:31' (first 16 chars)
function matchingCompoundPoint(
  prefixPoints: SeriesPoint[],
  prefixIdx: number,
  compoundPoints: SeriesPoint[],
  compoundIdx: number,
):
  | false
  | {
      point: SeriesPoint;
      index: number;
    } {
  const len = 16; // 'YYYY-MM-HHThh:mm'
  const timeIdx = 0;

  const prefixTime = prefixPoints[prefixIdx][timeIdx].substring(0, len);

  // Loop through compound stats to find matching index,
  // starting from previous index
  for (let i = compoundIdx; i < compoundPoints.length; i++) {
    const compoundTime = compoundPoints[i][timeIdx].substring(0, len);
    if (compoundTime === prefixTime) {
      return {
        point: compoundPoints[i],
        index: i,
      };
    }
  }

  return false; // no match found
}

// Calculate average granularity for a series
// granularity is the time between points
export function getGranularity(points: SeriesPoint[]) {
  const count = 5; // get average of first 5 points
  let intervals = 0;
  for (let i = 0; i < count; i++) {
    intervals += Date.parse(points[i + 1][0]) - Date.parse(points[i][0]);
  }
  return intervals / count; // milliseconds
}

// Combine compound stats and processed stats series
//
// The closest processed stats point is always matched to the compound stats point
// being iterated over, meaning the same processed stats point could be reused
// and matched to multiple compound stats points.
export function combineCompoundAndProcessedStatsPoints(
  compoundPoints: SeriesPoint[],
  processedStatsPoints: SeriesPoint[],
) {
  // If there are no processed stats values, fill out the series with null values
  if (processedStatsPoints.length < 1) {
    return compoundPoints.map(cPoint => {
      const nulls = COMPOUND_METRICS_PROCESSED_STATS.map(() => null);
      return [...cPoint, ...nulls];
    });
  }

  const timeIdx = 0; // time always first item in a series

  let psStartIdx = 0;

  // Loop through all compound stats points in the series
  // and find matching processed stats points
  return compoundPoints.map(cPoint => {
    // Get timestamp for arithmetic
    const cTime = new Date(cPoint[timeIdx]).getTime();

    // Continue looping through process stats point for a match to the current
    // compound stats point
    for (let psIdx = psStartIdx; psIdx < processedStatsPoints.length; psIdx++) {
      const psTime = new Date(processedStatsPoints[psIdx][timeIdx]).getTime();

      // perfect match, no more to do here
      if (cTime === psTime) {
        psStartIdx = psIdx;
        break;
      }

      // no more processed stats points are available, use the last point
      if (!processedStatsPoints[psIdx + 1]) {
        psStartIdx = psIdx;
        break;
      }

      const nextPsTime = new Date(processedStatsPoints[psIdx + 1][timeIdx]).getTime();

      const diff = Math.abs(psTime - cTime);
      const nextDiff = Math.abs(nextPsTime - cTime);

      // if next time difference is greater than current, the current point is the closest
      if (nextDiff > diff) {
        psStartIdx = psIdx;
        break;
      }
      // else the next point is closer (better), so keep searching
    }

    return [...cPoint, ...processedStatsPoints[psStartIdx].slice(1)]; // slice time
  });
}

export function combineCompoundAndProcessedStats(compoundStats: Series, processedStats: Series) {
  // Combine columns
  compoundStats.columns.push(...processedStats.columns.slice(1)); // remove time

  // Combine values
  compoundStats.values = combineCompoundAndProcessedStatsPoints(compoundStats.values, processedStats.values);

  return compoundStats;
}

function calcPercent(
  prefixPoint: SeriesPoint,
  compoundPoint: SeriesPoint,
  metric: typeof PREFIX_COMPOUND_METRICS[number],
  prefixMetrics: string[],
  compoundMetrics: string[],
): number | null {
  const metricSuffix = metric.replace(PREFIX_COMPOUND_METRIC_PREFIX, "");
  const cVal = compoundPoint[compoundMetrics.indexOf(`compound_${metricSuffix}`)];

  if (cVal === 0) return 0; // don't divide by zero

  const pVal = prefixPoint[prefixMetrics.indexOf(`prefix_${metricSuffix}`)];

  if (cVal === null || pVal === null) return null;

  return pVal / cVal;
}

// Combine prefix and compound stats series' into a single series
export function combinePrefixAndCompoundStatsPoints(prefixSeries: Series, compoundSeries: Series): SeriesPoint[] {
  const prefixPoints = prefixSeries.values;
  const compoundPoints = compoundSeries.values;

  const pMetrics = prefixSeries.columns;
  const cMetrics = compoundSeries.columns.slice(1);

  // Used to fill out null compound stats point values when while combining
  // prefix and compound series.
  // Slice `1` to remove time, the combined series uses the prefix series' time.
  // Add metrics calculated here.
  const pointLen = cMetrics.length + PREFIX_COMPOUND_METRICS.length;

  // if the compound stats point does not exist, populate the combined point
  // with the appropriate amount of null values
  const compoundPointEmpty: SeriesPoint = [];
  for (let i = 0; i < pointLen; i++) {
    compoundPointEmpty.push(null);
  }

  let compoundStartingIndex = 0;

  return prefixPoints.map((prefixPoint, i) => {
    let compoundPoint = compoundPointEmpty;

    const matched = matchingCompoundPoint(prefixPoints, i, compoundPoints, compoundStartingIndex);

    if (matched) {
      compoundStartingIndex = matched.index;

      // Slice `1` to remove time, the combined series uses the prefix series' time
      compoundPoint = matched.point.slice(1);

      // Add points calculated using prefix and compound stats values here,
      // instead of in modules/debug-mode.ts, because here we have checked for,
      // and definitely have, prefix and compound stats values.
      // Checking and fetching within the fetchPrefixStats action would be convoluted

      for (const metric of PREFIX_COMPOUND_METRICS) {
        compoundPoint.push(calcPercent(prefixPoint, compoundPoint, metric, pMetrics, cMetrics));
      }
    }

    return [...prefixPoint, ...compoundPoint];
  });
}

export function combinePrefixAndCompoundStats(prefixStats: Series, compoundStats: Series) {
  // Combine columns
  prefixStats.columns.push(
    ...compoundStats.columns.slice(1), // slice to remove time
    ...PREFIX_COMPOUND_METRICS,
  );

  // Combine values
  prefixStats.values = combinePrefixAndCompoundStatsPoints(prefixStats, compoundStats);

  return prefixStats;
}
